<template>
  <div>
    <FilterOptions caption="SEARCH" icon="cil-filter" @filter-changed="handleFilter" />

    <CCard class="bg-style2 mt-3">
      <CCardHeader class="bg-gradient-dark text-white" style="border-top-left-radius: 1rem; border-top-right-radius: 1rem;">
        <span class="font-weight-bold h6">
          <CIcon v-if="icon" :name="icon" /> {{ caption }}
        </span>
        <div class="card-header-actions">Total: {{ filteredUsers.length }}</div>
      </CCardHeader>

      <CCardBody>
        <CRow>
          <CCol>
            <CDataTable
                :table-filter="{label:''}"
                :hover="hover"
                :striped="striped"
                :bordered="bordered"
                :small="small"
                :fixed="fixed"
                :dark="dark"
                :loading="loading"
                :items="filteredUsers"
                :fields="filteredFields"
                :items-per-page="20"
                pagination
            >
              <template #index="{ index }">
                <td class="text-center">{{ index + 1 }}</td>
              </template>

              <template #status="{ item }">
                <td>
                  <CBadge :color="getStatusColor(item.status)">
                    {{ item.status }}
                  </CBadge>
                </td>
              </template>

              <template #action="{ item }">
                <td>
                  <div class="d-flex flex-wrap align-items-center">
                    <!-- View Candidate Detail -->
                    <CButton
                        v-if="item.role === 'candidate'"
                        size="sm"
                        color="primary"
                        variant="outline"
                        shape="pill"
                        class="me-2 mb-2 mr-2"
                        @click="viewCandidateDetail(item)"
                    >
                      <CIcon name="cil-description" />
                    </CButton>

                    <!-- ✅ Updated Delete button -->
                    <CButton
                        v-if="isSuperAdmin && item._id !== currentUser._id"
                        size="sm"
                        color="danger"
                        shape="pill"
                        variant="outline"
                        class="me-2 mb-2 mr-2"
                        @click="onRemove(item)"
                    >
                      <CIcon name="cil-trash" />
                    </CButton>

                    <!-- Change Role button -->
                    <CButton
                        v-if="isSuperAdmin"
                        size="sm"
                        color="info"
                        shape="pill"
                        variant="outline"
                        class="me-2 mb-2 mr-2"
                        @click="onChangeRole(item)"
                    >
                      <CIcon name="cil-pencil" />
                    </CButton>
                  </div>
                </td>
              </template>
            </CDataTable>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Modal for Candidate Detail -->
    <CModal
        :show.sync="candidateModalVisible"
        :title="'Candidate Registration Detail'"
        dialogClass="modal-dialog"
        style="width: 98%; max-width: 1400px; margin: 2rem auto;"
    >
      <template #header>
        <div class="d-flex justify-content-between w-100">
          <h5 class="modal-title">Candidate Registration Detail</h5>
          <CButton color="link" class="text-danger p-0 m-0" @click="candidateModalVisible = false">
            <CIcon name="cil-x" size="lg" />
          </CButton>
        </div>
      </template>

      <template #default>
        <div v-if="selectedCandidate">
          <CForm>
            <CRow class="mb-4">
              <CCol md="6" class="text-center">
                <a v-if="selectedCandidate.picture" :href="fullUrl(selectedCandidate.picture)" target="_blank">
                  <img :src="fullUrl(selectedCandidate.picture)" class="img-thumbnail" style="max-height:200px;" />
                </a>
                <div class="mt-2">Profile Photo</div>
              </CCol>
              <CCol md="6" class="text-center">
                <a v-if="selectedCandidate.transcript" :href="fullUrl(selectedCandidate.transcript)" target="_blank">
                  <img :src="fullUrl(selectedCandidate.transcript)" class="img-thumbnail" style="max-height:200px;" />
                </a>
                <div class="mt-2">Transcript</div>
              </CCol>
            </CRow>

            <CRow>
              <CCol md="6" class="mb-3"><CFormLabel>Name</CFormLabel><CInput :value="selectedCandidate.name" disabled /></CCol>
              <CCol md="6" class="mb-3"><CFormLabel>Student ID</CFormLabel><CInput :value="selectedCandidate.studentId" disabled /></CCol>
              <CCol md="6" class="mb-3"><CFormLabel>School</CFormLabel><CInput :value="selectedCandidate.department" disabled /></CCol>
              <CCol md="6" class="mb-3"><CFormLabel>Major</CFormLabel><CInput :value="selectedCandidate.major" disabled /></CCol>
              <CCol md="6" class="mb-3"><CFormLabel>Line ID</CFormLabel><CInput :value="selectedCandidate.line" disabled /></CCol>
              <CCol md="6" class="mb-3"><CFormLabel>GPAX</CFormLabel><CInput :value="selectedCandidate.gpax" disabled /></CCol>
            </CRow>

            <div class="mb-3">
              <CFormLabel>Policy</CFormLabel>
              <div class="p-3 border rounded" style="min-height:120px;"><div v-html="selectedCandidate.policy" /></div>
            </div>

            <div class="text-end">
              <span class="font-weight-bold text-muted">
                Status: {{ selectedCandidate.status }}
              </span>
            </div>
          </CForm>
        </div>
      </template>
    </CModal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import FilterOptions from '@/projects/views/admin/component/filter_select.vue'
import api from '@/service/api.js'
import { CIcon } from '@coreui/vue'
import Swal from 'sweetalert2'

export default {
  name: 'AdminUserList',
  components: { FilterOptions, CIcon },
  props: {
    icon: { type: String, default: 'cil-settings' },
    caption: { type: String, default: 'List of System Users' },
    hover: Boolean, striped: Boolean, bordered: Boolean,
    small: Boolean, fixed: Boolean, dark: Boolean,
    loading: { type: Boolean, default: false }
  },
  data() {
    return {
      users: [],
      filters: {
        status: null,
        role: null,
        userType: null
      },
      candidateModalVisible: false,
      selectedCandidate: null
    }
  },
  computed: {
    ...mapGetters({ currentUser: 'auth/user' }),
    isSuperAdmin() {
      return this.currentUser?.role?.role_name === 'superadmin';
    },
    filteredUsers() {
      return this.users.filter(user => {
        return (
            (!this.filters.status || user.status === this.filters.status.value) &&
            (!this.filters.role || user.role === this.filters.role.value) &&
            (!this.filters.userType || user.candidateType === this.filters.userType.value)
        );
      });
    },
    filteredFields() {
      const base = [
        { key: 'index', label: 'No.', _style: 'width:60px; text-align:center' },
        { key: 'name', label: 'Name' }
      ];
      if (this.filters.role?.value !== 'candidate') {
        base.push({ key: 'email', label: 'Email' });
      }

      const extended = [
        { key: 'studentId', label: 'Student ID' },
        { key: 'department', label: 'Department' },
        ...(this.filters.role?.value === 'user' ? [] : [{ key: 'major', label: 'Major' }])
      ];

      const candidateExtra = [
        { key: 'candidateType', label: 'Candidate Type' },
        { key: 'status', label: 'Status' }
      ];

      const final = [
        { key: 'role', label: 'Role' },
        ...(this.isSuperAdmin ? [{ key: 'action', label: 'Actions', _style: 'width:180px' }] : [])
      ];
      const role = this.filters.role?.value;
      if (role === 'admin' || role === 'committee'|| role === 'superadmin') {
        return [...base, ...final];
      } else if (role === 'user') {
        return [...base, ...extended, ...final];
      } else {
        return [...base, ...extended, ...candidateExtra, ...final];
      }
    }
  },
  methods: {
    async onInit() {
      try {
        const res = await api.candidate('getCandidates');
        const candidates = res?.data?.data || [];

        const formatted = candidates
            .filter(c => c.accountID && c.type && c.status)
            .map(c => ({
              _id: c.accountID._id,
              name: c.accountID.name || '-',
              email: c.email || '-',
              studentId: c.accountID.studentId || '-',
              department: c.accountID.department?.department_name || '-',
              major: c.accountID.major?.major_name || '-',
              role: c.accountID.role?.role_name || 'candidate',
              candidateType: c.type?.type_name || '-',
              status: c.status?.status_name || 'Unknown',
              picture: c.picture || '',
              transcript: c.transcript || '',
              gpax: c.gpax || '',
              line: c.line || '',
              policy: c.policy || ''
            }));

        const registeredIds = formatted.map(c => c._id);

        const staffRes = await api.members('get');
        const staff = staffRes?.data?.data || [];

        const filteredStaff = staff
            .filter(u => !registeredIds.includes(u._id))
            .map(u => ({
              _id: u._id,
              name: u.name || '-',
              email: u.email || '-',
              studentId: u.studentId || '-',
              department: u.department?.department_name || '-',
              major: u.major?.major_name || '-',
              role: u.role?.role_name || '-',
              candidateType: '-',
              status: '-'
            }));

        this.users = [...formatted, ...filteredStaff];
      } catch (e) {
        console.error('Error fetching users:', e);
      }
    },
    viewCandidateDetail(user) {
      this.selectedCandidate = user;
      this.candidateModalVisible = true;
    },
    fullUrl(path) {
      return path ? `https://election-su.mfu.ac.th${path}` : '';
    },
    getStatusColor(status) {
      switch (status) {
        case 'Approved': return 'success';
        case 'Declined': return 'danger';
        case 'Pending': return 'warning';
        default: return 'secondary';
      }
    },
    async onRemove(user) {
      if (!this.isSuperAdmin || user.role === 'superadmin') {
        return Swal.fire({
          icon: 'info',
          title: 'Not allowed',
          text: 'Only superadmin can delete users, and not other superadmins.'
        });
      }

      // Prevent deleting self
      if (this.currentUser._id === user._id) {
        return Swal.fire({
          icon: 'warning',
          title: 'Action Denied',
          text: 'You cannot delete your own account.'
        });
      }

      const result = await Swal.fire({
        title: `Are you sure?`,
        text: `Do you want to delete ${user.role} "${user.name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e55353',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      });

      if (result.isConfirmed) {
        try {
          await api.roles('delete', { id: user._id });
          await Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: `${user.name} has been deleted.`,
            timer: 2000,
            showConfirmButton: false
          });
          this.onInit();
        } catch (e) {
          console.error(`Failed to delete ${user.role}:`, e);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: `Failed to delete ${user.role}. Please try again.`
          });
        }
      }
    },
    async onChangeRole(user) {
      const roleOptions = {
        '685e4043e9f2fe21e0c6d029': 'user',
        '685e404ce9f2fe21e0c6d02c': 'committee',
        '685e405ae9f2fe21e0c6d02f': 'candidate',
        '68623a7428c185582cb41ff1': 'admin',
        '6890e984809712b5a6b2badb': 'superadmin'
      }

      const { value: selectedRole } = await Swal.fire({
        title: `Change role for ${user.name}`,
        input: 'select',
        inputOptions: roleOptions,
        inputPlaceholder: 'Select a new role',
        showCancelButton: true,
        confirmButtonText: 'Change',
        cancelButtonText: 'Cancel',
        customClass: {
          title: 'custom-swal-title',
          input: 'custom-swal-select'
        },
        didOpen: () => {
          const titleEl = document.querySelector('.custom-swal-title')
          const selectEl = document.querySelector('.custom-swal-select')
          const confirmBtn = document.querySelector('.swal2-confirm')
          const cancelBtn = document.querySelector('.swal2-cancel')
          if (titleEl) titleEl.style.fontSize = '1.25rem'
          if (selectEl) {
            selectEl.style.appearance = 'none'
            selectEl.style.backgroundColor = '#fff'
            selectEl.style.border = '1px solid #ced4da'
            selectEl.style.borderRadius = '0.375rem'
            selectEl.style.padding = '0.5rem 2.5rem 0.5rem 0.75rem'
            selectEl.style.fontSize = '1rem'
            selectEl.style.color = '#495057'
            selectEl.style.backgroundImage =
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='gray' class='bi bi-caret-down-fill' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C2.077 5.252 2.38 4.5 2.966 4.5h10.068c.586 0 .889.752.515 1.158L8.753 11.14a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E\")"
            selectEl.style.backgroundRepeat = 'no-repeat'
            selectEl.style.backgroundPosition = 'right 0.75rem center'
            selectEl.style.backgroundSize = '1rem'
          }
          if (confirmBtn) confirmBtn.style.fontSize = '0.9rem'
          if (cancelBtn) cancelBtn.style.fontSize = '0.9rem'
        },
        inputValidator: (value) => {
          return !value ? 'You must select a role' : null
        }
      });

      if (selectedRole) {
        try {
          await api.roles('put', {
            id: user._id,
            role: selectedRole
          });

          await Swal.fire({
            icon: 'success',
            title: 'Updated!',
            text: `${user.name}'s role has been changed.`,
            timer: 2000,
            showConfirmButton: false
          });

          this.onInit();
        } catch (err) {
          console.error('[Change Role Error]', err);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to change role. Please try again.'
          });
        }
      }
    },
    handleFilter(filters) {
      this.filters = filters;
    }
  },
  created() {
    this.onInit();
  }
}
</script>


<style scoped>
.detail-modal-card .modal-content {
  padding: 2rem;
  font-size: 1rem;
}

.detail-modal-card .modal-title {
  font-size: 1.5rem;
  font-weight: bold;
}

.detail-modal-card .img-thumbnail {
  max-height: 250px;
  border-radius: 0.75rem;
  border: 2px solid #dee2e6;
}
.detail-modal-card .border.rounded {
  background-color: #f9f9f9;
  padding: 1rem;
  font-size: 0.95rem;
  line-height: 1.5;
  border-radius: 0.5rem;
}
.detail-modal-card input,
.detail-modal-card textarea {
  font-size: 1rem;
  padding: 0.6rem 1rem;
  border-radius: 0.5rem;
}
::v-deep .modal-dialog {
  max-width: 800px !important;
  width: 98% !important;
  margin: 2rem auto !important;
}

::v-deep .modal-content {
  width: 100% !important;
}

.policy-preview .policy-content img {
  width: 100%;
  height: 70%;
  object-fit: contain; /* รักษาสัดส่วนภาพ */
  border-radius: 0.5rem;
  display: block;
  margin: 0.5rem auto;
}
.policy-preview {
  max-height: 400px;
  overflow-y: auto;
}
@media (max-width: 768px) {
  .modal-content {
    width: 95% !important;
  }

  .policy-preview {
    font-size: 0.9rem;
  }
}
.policy-preview .policy-content img {
  width: 100%;
  height: 70%;
  object-fit: contain;
  display: block;
  margin: auto;
}
.swal2-select {
  padding-right: 2.5rem !important;
  background-position: right 1rem center !important;
  background-size: 0.8rem !important;
}

.swal2-title {
  font-size: 1.5rem !important;
  font-weight: 600 !important;
}
</style>


